/**
 * ReviewPromptDialog
 *
 * Checks for a due-review prompt ONCE per browser session on mount.
 * After a "Snooze" it re-checks exactly once after the snooze period.
 * After a "No" it sets a sessionStorage flag and never prompts again
 * for the rest of the session.
 *
 * Behaviour:
 *  - "Yes"    → navigates to the generated passage
 *  - "No"     → warning toast + session blocked (no more prompts this login)
 *  - "Snooze" → hides dialog, re-checks once after 5 min
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, AlertTriangle, BookOpen, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SNOOZE_RECHECK_MS = 5 * 60 * 1000; // re-check once after 5-min snooze
const MAX_SNOOZES = 2;
const SESSION_DECLINED_KEY = 'iling_review_prompt_declined';

export default function ReviewPromptDialog() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLang, setSelectedLang] = useState(null);
  const snoozeTimerRef = useRef(null);

  const loadPrompt = async () => {
    // Never show on oracle embed page or if "No" was clicked this session
    if (window.location.pathname === '/oracle-embed') return;
    if (sessionStorage.getItem(SESSION_DECLINED_KEY)) return;
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/review-notifications/check`,
        { withCredentials: true }
      );
      const data = res.data || {};
      if (data.has_prompt && data.prompt) {
        setPrompt(data.prompt);
        setSelectedLang(data.prompt.language_code || null);
      }
    } catch {
      // Silent — auth gates and 5xx shouldn't spam the UI.
    }
  };

  // Check ONCE on mount (once per browser session)
  useEffect(() => {
    if (!sessionStorage.getItem(SESSION_DECLINED_KEY)) {
      loadPrompt();
    }
    return () => {
      if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current);
    };
  }, []);

  const respond = async (decision) => {
    if (!prompt || submitting) return;
    setSubmitting(true);
    try {
      const payload = { decision };
      if (decision === 'yes' && selectedLang) {
        payload.language_code = selectedLang;
      }
      const res = await axios.post(
        `${BACKEND_URL}/api/review-notifications/${prompt.prompt_id}/respond`,
        payload,
        { withCredentials: true }
      );
      const data = res.data || {};
      if (data.ok === false) {
        toast.error(data.message || 'Could not record your choice.');
        return;
      }
      if (data.action === 'snoozed') {
        const remaining = data.snoozes_remaining || 0;
        toast.info(
          remaining > 0
            ? `Snoozed for 5 minutes. (${remaining} snooze${remaining === 1 ? '' : 's'} left)`
            : 'Final snooze — next prompt will be Yes or No only.'
        );
        setPrompt(null);
        // Re-check exactly ONCE after the snooze period
        snoozeTimerRef.current = setTimeout(loadPrompt, SNOOZE_RECHECK_MS);
        return;
      }
      if (data.action === 'declined') {
        // Block for the rest of this session
        sessionStorage.setItem(SESSION_DECLINED_KEY, '1');
        setPrompt(null);
        toast.warning(data.warning || 'Skipping a scheduled review affects retention.', {
          duration: 8000,
        });
        return;
      }
      if (data.action === 'accepted' && data.text_id) {
        toast.success(`Generated a ${data.word_count}-word passage`);
        setPrompt(null);
        navigate(`/reader/${data.text_id}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || 'Network error. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!prompt) return null;

  const snoozeCount = prompt.snooze_count || 0;
  const canSnooze = snoozeCount < MAX_SNOOZES;
  const overload = prompt.overload === true;
  const breakdown = Array.isArray(prompt.language_breakdown) ? prompt.language_breakdown : [];
  const activeLang = selectedLang || prompt.language_code;
  const activeEntry = breakdown.find((b) => b.language_code === activeLang);
  const activeCount = activeEntry ? activeEntry.due_count : prompt.due_word_count;
  const activeName = activeEntry?.name || (activeLang || '').toUpperCase();
  const showPicker = breakdown.length > 1;

  return (
    <Dialog
      open={!!prompt}
      onOpenChange={(open) => {
        if (!open) {
          // Closing without choosing is treated as snooze if still allowed;
          // otherwise force a Yes/No choice (no dismiss on prompt 3).
          if (canSnooze) respond('snooze');
        }
      }}
    >
      <DialogContent className="max-w-md" data-testid="review-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Review reading ready
          </DialogTitle>
          <DialogDescription className="space-y-2 pt-2">
            <span className="block">
              You have <b>{activeCount}</b> {activeName} words due for review.
              Want a short, custom passage that uses them in context?
            </span>
            {overload && (
              <span className="flex items-start gap-2 mt-3 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  You've accumulated <b>{prompt.total_due}</b> due words. Consider working on
                  same-level material for a session or two to prevent review-queue overload.
                </span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {showPicker && (
          <div className="mt-2" data-testid="review-prompt-language-picker">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Generate passage for:
            </div>
            <div className="flex flex-wrap gap-2">
              {breakdown.map((b) => {
                const isActive = b.language_code === activeLang;
                return (
                  <button
                    key={b.language_code}
                    type="button"
                    onClick={() => setSelectedLang(b.language_code)}
                    disabled={submitting}
                    data-testid={`review-prompt-lang-${b.language_code}`}
                    className={
                      'px-3 py-1.5 rounded-full text-xs border transition-colors ' +
                      (isActive
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted border-border')
                    }
                  >
                    <span className="font-medium">{b.name}</span>
                    {b.native_name && b.native_name !== b.name && (
                      <span className="opacity-70 ml-1">· {b.native_name}</span>
                    )}
                    <span className="ml-2 opacity-80">({b.due_count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            onClick={() => respond('no')}
            disabled={submitting}
            data-testid="review-prompt-no"
          >
            No
          </Button>
          {canSnooze && (
            <Button
              variant="outline"
              onClick={() => respond('snooze')}
              disabled={submitting}
              data-testid="review-prompt-snooze"
            >
              <Clock className="h-4 w-4 mr-1" /> Snooze 5 min
            </Button>
          )}
          <Button
            onClick={() => respond('yes')}
            disabled={submitting}
            data-testid="review-prompt-yes"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Yes, generate passage
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

